import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDevice,
  useFirmwareUpdate,
  useRemoveDeviceLink,
} from "hooks/useDevices";
import { trackEvent } from "utils/analytics";
import { useAuth } from "context/AuthContext";
import { getRoleHomePath } from "utils/paths";

import DeviceHeader from "components/device/DeviceHeader";
import DeviceSummary from "components/device/DeviceSummary";
import DeviceTabs from "components/device/DeviceTabs";
import DeviceConfirmationDialog from "components/device/DeviceConfirmationDialog";
import DeviceAddressMap from "components/DeviceAddressMap";
import { LoadingScreen } from "components/shared/LoadingScreen";
import { PageError } from "components/shared/PageError";

import { PageContainer } from "styles/devices/deviceDetails.styles";
import { DeviceRegion } from "types/device.types";
import { notify } from "utils/notify";

type UpdateType = "firmware" | "nipper" | null;

interface DeviceDetailsProps {
  serialNumber: string;
  linkId?: string;
}

export function DeviceDetails({ serialNumber, linkId }: DeviceDetailsProps) {
  const { data, isLoading, error, refetch } = useDevice(serialNumber, linkId);
  const firmwareUpdateMutation = useFirmwareUpdate();
  const removeDeviceMutation = useRemoveDeviceLink();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [updateType, setUpdateType] = useState<UpdateType>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addressDialogVisible, setAddressDialogVisible] = useState(false);

  useEffect(() => {
    if (serialNumber) {
      trackEvent("device_viewed", { serial_number: serialNumber });
    }
  }, [serialNumber]);

  const confirmUpdate = (type: UpdateType) => {
    setUpdateType(type);
    setConfirmDialogVisible(true);
  };

  const executeUpdate = async () => {
    if (!serialNumber || !data || !updateType) return;

    try {
      await firmwareUpdateMutation.mutateAsync({
        product: updateType === "firmware" ? "lifeguard" : "nipper",
        serialNumbers: [serialNumber],
      });
      trackEvent("firmware_update_started", {
        serial_number: serialNumber,
        update_type: updateType,
      });
      notify.success(
        `${updateType === "firmware" ? "Firmware" : "Nipper"} update initiated successfully`,
      );
    } catch (err) {
      console.error(`Error updating ${updateType}:`, err);
      notify.error(`Failed to update ${updateType}. Please try again later.`);
    } finally {
      setConfirmDialogVisible(false);
    }
  };

  const executeDelete = async () => {
    if (!serialNumber || !linkId) return;

    try {
      await removeDeviceMutation.mutateAsync(linkId);
      trackEvent("device_access_removed", { serial_number: serialNumber });
      notify.success("Device access removed from your account.");
      navigate(getRoleHomePath(user?.role));
    } catch (err) {
      console.error("Error removing device access:", err);
      notify.error("Failed to remove device access. Please try again later.");
    } finally {
      setDeleteDialogVisible(false);
    }
  };

  const openAddressDialog = useCallback(() => setAddressDialogVisible(true), []);
  const closeAddressDialog = useCallback(
    () => setAddressDialogVisible(false),
    [],
  );

  const dialogContent =
    updateType === "firmware"
      ? {
          title: "Confirm Firmware Update",
          message:
            "Are you sure you want to update the firmware of this device?",
        }
      : {
          title: "Confirm Nipper Update",
          message: "Are you sure you want to update the nipper of this device?",
        };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingScreen message="Loading device details..." />
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <PageError
          title="Error loading device"
          message="Error loading device details. Please try again later."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DeviceHeader
        serialNumber={serialNumber || ""}
        onUpdateClick={confirmUpdate}
        onDeleteClick={() => setDeleteDialogVisible(true)}
        isUpdating={firmwareUpdateMutation.isPending}
        isDeleting={removeDeviceMutation.isPending}
      />

      <DeviceSummary
        serialNumber={data.serialNumber}
        firmwareVersion={data.firmwareVersion}
        nipperVersion={data.nipperVersion}
        updatedAt={data.updatedAt}
        location={data.DeviceLocation}
        onEditAddressClick={openAddressDialog}
      />

      {serialNumber && (
        <DeviceTabs
          linkId={linkId}
          serialNumber={serialNumber}
          region={data.region === 1 ? DeviceRegion.EU : DeviceRegion.AU}
        />
      )}

      <DeviceConfirmationDialog
        visible={confirmDialogVisible}
        title={dialogContent.title}
        message={dialogContent.message}
        onHide={() => setConfirmDialogVisible(false)}
        onConfirm={executeUpdate}
        isProcessing={firmwareUpdateMutation.isPending}
      />

      <DeviceConfirmationDialog
        visible={deleteDialogVisible}
        title="Remove device access"
        message="This removes this Lifeguard from your dealer account. It does not delete the Lifeguard or customer data."
        icon={{
          className: "pi pi-exclamation-triangle",
          color: "var(--red-500)",
        }}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={executeDelete}
        isProcessing={removeDeviceMutation.isPending}
        confirmButtonLabel="Remove access"
        cancelButtonLabel="Keep access"
        confirmButtonClassName="p-button-danger"
      />

      {serialNumber && addressDialogVisible && (
        <DeviceAddressMap
          serialNumber={serialNumber}
          address={data.DeviceLocation?.address}
          latitude={data.DeviceLocation?.latitude}
          longitude={data.DeviceLocation?.longitude}
          refetchQuery={() => {
            refetch();
            closeAddressDialog();
          }}
          visible={addressDialogVisible}
          onClose={closeAddressDialog}
          zoom={15}
          hideButton
        />
      )}
    </PageContainer>
  );
}
